import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ClassesEnum } from "@/ts/enums/ClassesEnum";
import { getTrackingId } from "@/utils/utils";
import { useAtom } from "jotai";

import { Flex } from "@chakra-ui/react";

import { ChargeInterface } from "@ts/interfaces/Checkout/ChargeInterface";
import { CheckoutInterface } from "@ts/interfaces/Checkout/CheckoutInterface";

import {
    checkoutAtom,
    loadableProductFamiliesAtom
} from "@atoms/checkoutAtom.ts";

import ContentLayout from "@layouts/ContentLayout";

import { Card } from "@components/Card";
import { Feature } from "@components/Feature";
import { Skeleton } from "@components/Skeleton";
import { Text } from "@components/Text";

const PathwayContainer = () => {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const [productFamilies] = useAtom(loadableProductFamiliesAtom);
    const [checkout, setCheckout] = useAtom(checkoutAtom);

    const isPathwaySelected = !!checkout.charge;

    const onSelectCharge = (charge: ChargeInterface) => {
        setCheckout((prev: CheckoutInterface | Promise<CheckoutInterface>) => ({
            ...prev,
            charge,
            addon: null
        }));
    };

    const onGoBack = () => navigate("/checkout/select-age-group");

    const onGoNext = () => {
        if (checkout.charge?.addons) {
            navigate("/checkout/select-premium-services");
        } else {
            navigate("/checkout/payment");
        }
    };

    return (
        <ContentLayout
            title={t(
                "checkout.selectPathwayPage.title",
                "Which pathway you want your child to go through?"
            )}
            footerActionsProps={{
                canGoNext: isPathwaySelected,
                onGoBack,
                onGoNext,
                btnClassHierarchy: {
                    next: [ClassesEnum.PathwayContainer, ClassesEnum.Button],
                    back: [ClassesEnum.PathwayContainer, ClassesEnum.Button]
                },
                btnActions: {
                    next: "goNextFromPathwayPage",
                    back: "goBackFromPathwayPage"
                }
            }}
            className={ClassesEnum.PathwayContainer}
        >
            <Flex
                justify="center"
                direction={["column", null, "row"]}
                gap={[10, null, 12]}
            >
                {productFamilies.state === "hasData" &&
                checkout.productFamily?.charges
                    ? checkout.productFamily?.charges[0].charges.map(c => (
                          <Card<ChargeInterface>
                              key={c.id}
                              isHighlighted={c.isHighlighted}
                              highlightedText={c.highlightedText}
                              item={c}
                              isActive={c.id === checkout.charge?.id}
                              onClick={onSelectCharge}
                              width={[288, null, 363]}
                              height={["auto", null, 419]}
                              action={`pathway-${c.id}`}
                              classHierarchy={[
                                  ClassesEnum.PathwayContainer,
                                  ClassesEnum.Card
                              ]}
                          >
                              <Flex
                                  direction="column"
                                  gap={4}
                                  marginBottom="auto"
                                  p={[1, null, 0]}
                              >
                                  <Flex
                                      direction="column"
                                      gap={2}
                                      align="center"
                                  >
                                      <Text
                                          fontSize={40}
                                          lineHeight="45px"
                                          textAlign="center"
                                          type="heading"
                                          data-tracking-id={getTrackingId(
                                              [
                                                  ClassesEnum.PathwayContainer,
                                                  ClassesEnum.Card,
                                                  ClassesEnum.Text
                                              ],
                                              `pathwayName-${c.id}`
                                          )}
                                      >
                                          {c.name}
                                      </Text>
                                      <Text
                                          fontSize={[14, null, 18]}
                                          type="subHeading"
                                      >
                                          {c.priceLabel}
                                      </Text>
                                      <Text
                                          fontSize={14}
                                          color="gray.600"
                                          fontWeight="bold"
                                      >
                                          {c.description}
                                      </Text>
                                  </Flex>
                                  <Flex direction="column" gap={2}>
                                      {c.features.map(f => (
                                          <Feature
                                              key={f.id}
                                              isActive={f.isAvailable}
                                              text={f.text}
                                              data-tracking-id={getTrackingId(
                                                  [
                                                      ClassesEnum.PathwayContainer,
                                                      ClassesEnum.Card
                                                  ],
                                                  `pathwayFeature-${f.id}`
                                              )}
                                          />
                                      ))}
                                  </Flex>
                              </Flex>
                          </Card>
                      ))
                    : null}
                {productFamilies.state === "loading" && <Skeleton />}
            </Flex>
        </ContentLayout>
    );
};

export default PathwayContainer;
